import { useState, useMemo, memo } from "react";
import { Search, ChevronDown, Minus } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";

interface MultiSelectFilterProps {
  title: string;
  options: string[];
  selectedValues: string[];
  onChange: (values: string[]) => void;
  onCommit?: () => void;
  placeholder?: string;
  className?: string;
}

export const MultiSelectFilter = memo(
  ({
    title,
    options,
    selectedValues,
    onChange,
    onCommit,
    placeholder = "Select",
    className,
  }: MultiSelectFilterProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");

    const handleOpenChange = (open: boolean) => {
      setIsOpen(open);
      if (!open && onCommit) {
        onCommit();
      }
    };

    const filteredOptions = useMemo(() => {
      if (!searchQuery) return options;
      return options.filter((opt) =>
        opt.toLowerCase().includes(searchQuery.toLowerCase()),
      );
    }, [options, searchQuery]);

    const isAllFilteredSelected = useMemo(() => {
      if (filteredOptions.length === 0) return false;
      return filteredOptions.every((opt) => selectedValues.includes(opt));
    }, [filteredOptions, selectedValues]);

    const handleToggleAll = () => {
      if (isAllFilteredSelected) {
        const newValues = selectedValues.filter(
          (val) => !filteredOptions.includes(val),
        );
        onChange(newValues);
      } else {
        const newValues = Array.from(
          new Set([...selectedValues, ...filteredOptions]),
        );
        onChange(newValues);
      }
    };

    const handleToggleOption = (option: string, checked: boolean) => {
      if (checked) {
        onChange([...selectedValues, option]);
      } else {
        onChange(selectedValues.filter((val) => val !== option));
      }
    };

    const displayValue =
      selectedValues.length > 0
        ? `${selectedValues.length} selected`
        : placeholder;

    const hasSelection = selectedValues.length > 0;

    return (
      <div className={cn("flex flex-col gap-1.5", className)}>
        <label className="text-xs font-medium text-muted-foreground">
          {title}
        </label>

        <Popover open={isOpen} onOpenChange={handleOpenChange}>
          <PopoverTrigger asChild>
            <button
              type="button"
              className={cn(
                "flex w-full cursor-pointer items-center justify-between rounded-[18px] border border-border bg-muted/60 px-3 py-2 text-sm font-medium text-muted-foreground hover:bg-muted focus:outline-none focus:ring-1 focus:ring-ring",
                hasSelection && "font-medium text-foreground",
              )}
            >
              <span className="truncate">{displayValue}</span>
              <ChevronDown className="h-4 w-4 opacity-50" />
            </button>
          </PopoverTrigger>

          <PopoverContent
            className="w-[240px] rounded-xl border-border p-2"
            align="start"
          >
            <div className="mb-2 flex items-center gap-2 rounded-md border border-border bg-card px-3 py-2">
              <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
              <input
                type="text"
                placeholder="search"
                className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>

            <div className="flex flex-col">
              {filteredOptions.length > 0 && (
                <div
                  className="mb-1 flex cursor-pointer items-center gap-3 rounded-md border-b border-border px-3 py-2 transition-colors hover:bg-muted"
                  onClick={handleToggleAll}
                >
                  <div
                    className={cn(
                      "flex h-4 w-4 shrink-0 items-center justify-center rounded-sm border border-border",
                      isAllFilteredSelected
                        ? "bg-muted text-foreground"
                        : "bg-card",
                    )}
                  >
                    {isAllFilteredSelected && <Minus className="h-3 w-3" />}
                  </div>
                  <span className="text-sm font-medium text-foreground select-none">
                    {isAllFilteredSelected ? "Deselect All" : "Select All"}
                  </span>
                </div>
              )}

              <div className="hide-scrollbar max-h-[220px] space-y-0.5 overflow-y-auto px-1">
                {filteredOptions.length === 0 ? (
                  <div className="py-4 text-center text-sm text-muted-foreground">
                    No results found.
                  </div>
                ) : (
                  filteredOptions.map((option) => {
                    const isSelected = selectedValues.includes(option);
                    return (
                      <div
                        key={option}
                        className="flex cursor-pointer items-center gap-3 rounded-md px-2 py-1.5 hover:bg-muted"
                        onClick={() =>
                          handleToggleOption(option, !isSelected)
                        }
                      >
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={() => {}}
                          className="rounded-[4px] border-border data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground"
                        />
                        <span
                          className={cn(
                            "text-sm select-none",
                            isSelected
                              ? "font-medium text-foreground"
                              : "text-foreground/80",
                          )}
                        >
                          {option}
                        </span>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </PopoverContent>
        </Popover>
      </div>
    );
  },
);

MultiSelectFilter.displayName = "MultiSelectFilter";
